import { type BrewStage, daysApart, laterStage } from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { Flame, TriangleAlert } from "lucide-react";
import { type BrewDetail, type BrewSummary, useBrew, useBrews } from "../api";
import { showDate, today } from "../format";
import { Menu } from "./ui";

/** How often the pills look again, so a brew moved on from another device shows here. */
const LIVE_MS = 30_000;
/** Pills beside the search on a wide screen; any more are counted, and listed in a menu. */
const SHOWN = 3;

/** Where the readings taken so far put a brew: the latest one decides (Tyler, 2026-10-02). */
export function readingsStage(readings: BrewDetail["readings"]): BrewStage {
  if (readings.og !== null || readings.fermenterLiters !== null) return "fermenter";
  if (
    readings.preBoilGravity !== null ||
    readings.preBoilLiters !== null ||
    readings.whirlpoolTemps.length > 0
  ) {
    return "boil";
  }
  if (readings.strikeTempC !== null || readings.mashTempC !== null || readings.mashPh !== null) {
    return "mash";
  }
  return "pull";
}

/**
 * A stage's name, in the header's pill and brew day's strip alike (the re-critique, 2026-10-07: the pill
 * said "Pulling" and "Into the fermenter" where the strip said Pull and Fermenter).
 */
export const STAGE_LABELS: Record<BrewStage, string> = {
  pull: "Pull",
  mash: "Mash",
  boil: "Boil",
  fermenter: "Fermenter",
};

/**
 * Where a brew in progress has got to: the stage its brewer said, or further on if a reading says so
 * (Tyler, 2026-10-06: said with one tap on brew day's strip, where it was only ever read from the
 * readings, and a brew sat at "Pulling" for days).
 */
export function brewStage(brew: Pick<BrewDetail, "readings" | "stage"> | undefined): string {
  if (!brew) return "In progress";
  return STAGE_LABELS[laterStage(brew.stage, readingsStage(brew.readings)) ?? "pull"];
}

/** Days since a brew's brew day, by the calendar. */
export const daysSince = (brewedOn: string) => daysApart(brewedOn, today());

/** "today", "yesterday", "4 days ago". */
export const ago = (days: number) =>
  days === 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;

/**
 * Brews in progress, beside the search on every page, each opening its brew day. Planned brews and
 * brewed ones are not shown: only a brew whose day has started (Tyler, 2026-10-02). Those that do not
 * fit open from a menu, since the Brew day list they once led to left with the Calendar (Scott's Jam,
 * 2026-10-08).
 */
export function BrewPills() {
  const { data: brews = [] } = useBrews({ refetchInterval: LIVE_MS });
  const live = brews.filter((b) => b.status === "draft");
  if (live.length === 0) return null;
  const first = live[0] as BrewSummary;
  const more = live.length - SHOWN;
  // The phone's one pill has no room for an age, so a brew sat a day or more is marked on it and said.
  const stale = live.filter((b) => daysSince(b.brewedOn) >= 1).length;
  const flag = stale ? <TriangleAlert size={14} className="brew-pill-flag" aria-hidden /> : null;
  return (
    <nav className="brew-pills" aria-label="Brews in progress">
      <div className="brew-pills-wide">
        {live.slice(0, SHOWN).map((b) => (
          <BrewPill key={b.id} brew={b} />
        ))}
        {more > 0 ? (
          <Menu
            label="More brews in progress"
            trigger={(opener) => (
              <button type="button" className="brew-pill more" {...opener}>
                +{more} more
              </button>
            )}
          >
            {(close) =>
              live.slice(SHOWN).map((b) => <BrewItem key={b.id} brew={b} onPick={close} />)
            }
          </Menu>
        ) : null}
      </div>
      {/* A phone has room for one small pill: the brew itself, or a menu of them when there are several. */}
      {live.length === 1 ? (
        <Link
          to="/brews/$brewId"
          params={{ brewId: first.id }}
          className="brew-pill compact"
          title={`${first.recipe.name}, brewing${stale ? `, brew day ${ago(daysSince(first.brewedOn))}, not marked Brewed` : ""}`}
        >
          <Flame size={16} aria-hidden />
          {flag}
          <span className="sr-only">
            {first.recipe.name}, brewing
            {stale ? `, brew day ${ago(daysSince(first.brewedOn))}, not marked Brewed` : ""}
          </span>
        </Link>
      ) : (
        <Menu
          label="Brews in progress"
          trigger={(opener) => (
            <button
              type="button"
              className="brew-pill compact"
              title={`${live.length} brews in progress${stale ? `, ${stale} not marked Brewed a day on` : ""}`}
              {...opener}
            >
              <Flame size={16} aria-hidden />
              <span className="brew-pill-count">{live.length}</span>
              {flag}
              <span className="sr-only">
                {" "}
                brews in progress{stale ? `, ${stale} not marked Brewed a day on` : ""}
              </span>
            </button>
          )}
        >
          {(close) => live.map((b) => <BrewItem key={b.id} brew={b} onPick={close} />)}
        </Menu>
      )}
    </nav>
  );
}

/**
 * A brew in progress: its name and stage, and once its brew day is a day gone, how long, in place of
 * the pulse that says it is live.
 */
function BrewPill({ brew }: { brew: BrewSummary }) {
  const { data } = useBrew(brew.id, { refetchInterval: LIVE_MS });
  const stage = brewStage(data);
  const days = daysSince(brew.brewedOn);
  const stale = days >= 1;
  return (
    <Link
      to="/brews/$brewId"
      params={{ brewId: brew.id }}
      className={`brew-pill ${stale ? "stale" : ""}`}
      activeProps={{ "aria-current": "page" }}
      title={`${brew.recipe.name} #${brew.number}: ${stage}, brew day ${showDate(brew.brewedOn)}${stale ? `, ${ago(days)}, not marked Brewed` : ""}. Open its brew day.`}
    >
      <span className="brew-pill-dot" aria-hidden />
      <span className="brew-pill-name">
        {brew.recipe.name} <span className="brew-pill-number">#{brew.number}</span>
      </span>
      <span className="brew-pill-stage">{stage}</span>
      {stale ? (
        <span className="brew-pill-age">
          <TriangleAlert size={13} aria-hidden /> {days === 1 ? "1 day" : `${days} days`}
          <span className="sr-only">, not marked Brewed</span>
        </span>
      ) : null}
    </Link>
  );
}

/** A brew in progress in the pills' menu: its name, number and stage, and its age once a day gone. */
function BrewItem({ brew, onPick }: { brew: BrewSummary; onPick: () => void }) {
  const { data } = useBrew(brew.id, { refetchInterval: LIVE_MS });
  const days = daysSince(brew.brewedOn);
  return (
    <Link
      to="/brews/$brewId"
      params={{ brewId: brew.id }}
      className="menu-item brew-menu-item"
      role="menuitem"
      onClick={onPick}
    >
      <span>
        <strong>{brew.recipe.name}</strong> #{brew.number}
        <span className="muted">
          {" "}
          {brewStage(data)}
          {days >= 1 ? `, ${ago(days)}, not marked Brewed` : ""}
        </span>
      </span>
      {days >= 1 ? <TriangleAlert size={14} className="brew-menu-flag" aria-hidden /> : null}
    </Link>
  );
}
